/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../lib/@waca/core-client/js/core-client/ui/core/View',
	'../lib/@waca/core-client/js/core-client/ui/KeyCodes',
	'../widgets/livewidget/nls/StringResources',
	'../visualizations/vipr/VIPRConfig',
	'../visualizations/vipr/extendedVis/SchematicVis',
	'../util/VidaSdkUtil',
	'text!./templates/LoadingIndicator.html',
	'underscore',
	'jquery',
	'doT',
	'react',
	'react-dom',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'./components/VisualizationContentContainer',
	'./components/custom/CustomPanelContainer',
	'./components/util/CustomVisAction',
	'hammerjs',
	'jquery.hammer'
], function(
	View,
	KeyCodes,
	StringResources,
	VIPRConfig,
	SchematicVis,
	VidaSdkUtil,
	LoadingIndicatorTemplate,
	_,
	$,
	dot,
	React,
	ReactDOM,
	PropTypes,
	UI_Toolkit,
	VisualizationContentContainer,
	CustomPanelContainer,
	CustomVisAction
) {

	const { Component } = React;
	const { Container, ProgressIndicator } = UI_Toolkit;
	const { Tabs, TabPanel , VSpacer, Label, FlexLayout, FlexItem } = UI_Toolkit;

	const DEFAULT_SELECTED_TAB = 'System';

	class VisualizationTabs extends Component {
		constructor (...args) {
			super(...args);
			this.state = {
				selectedTab: this.props.selectedTab,
				isRefreshing: false,
				deleteable: false,
				editable: false,
				addable: false,
				align: 'center',
				tabs: this.props
			};
		}

		render () {
			const panels = this._renderTabPanels();
			const tabsClassNames = panels.length === 1 ? 'vis-panel-tabs hide-tabs' : 'vis-panel-tabs';
			return (
				<Container className='vis-panel-container'>
					<VSpacer size={2} />
					<Label className='vis-panel-banner' label={StringResources.get('visualizationSlideoutTitle')}/>
					<VSpacer size={2} />
					<Tabs className={tabsClassNames}
						selected={this.state.selectedTab}
						onChange={this.onChange.bind(this)}
					>
						{panels}
					</Tabs>
				</Container>
			);
		}

		_renderTabPanels () {
			const panels = [];
			panels.push(<TabPanel
				id={DEFAULT_SELECTED_TAB}
				label={StringResources.get('extVisSystem')}
			>
				<VisualizationContentContainer
					containerClass='vis-system-container'
					itemClass='prop-item'
					items={this.props.tabs.system.items}
					handlers={this.props.tabs.handlers}
				/>
			</TabPanel>);

			const { customVisAction } = this.props;
			if (customVisAction.isEnabled()) {
				panels.push(<TabPanel
					id={'Custom'}
					label={StringResources.get('extVisCustom')}
				>
					{this._renderCustomPanelContainer()}
				</TabPanel>);
			}
			return panels;
		}

		onChange (tabId) {
			const { customVisAction } = this.props;
			const isRefreshing = customVisAction.shouldRefreshCustomPanel() && tabId === 'Custom';

			this.setState({
				selectedTab: tabId,
				isRefreshing: isRefreshing
			});

			if (isRefreshing) {
				return customVisAction.refreshCustomVis(true)
					.then((tabs = {}) => {
						this.setState({
							tabs: tabs,
							isRefreshing: false
						});
					});
			}
		}

		_renderProgressIndicator () {
			return (
				<FlexLayout
					fullHeight
					direction="column"
					justifyContent='center'
					width='100%'
					height='100%'
				>
					<FlexItem alignSelf='center'>
						<ProgressIndicator />
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderCustomPanelContainer () {
			const { dashboardApi, customVisAction, tabs } = this.props;
			const { isRefreshing } = this.state;


			return (
				<CustomPanelContainer
					custom={tabs.custom}
					handlers={tabs.handlers}
					dashboardApi={dashboardApi}
					customVisAction={customVisAction}
					isRefreshing={isRefreshing}
				/>
			);
		}
	}

	VisualizationTabs.propTypes = {
		tabs: PropTypes.object.isRequired,
		dashboardApi: PropTypes.object.isRequired,
		selectedTab: PropTypes.string.isRequired,
		customVisAction: PropTypes.object.isRequired
	};

	VisualizationTabs.defaultProps = {
		selectedTab: DEFAULT_SELECTED_TAB,
		isRefreshing: false
	};

	const SKD_VIS_PREVIEW = 'visualizationPreview';
	const CUS_VIS_SCHEMATIC_PREVIEW ='com.ibm.vis.schematicsPreview';

	const _geVisItemSelector = (classSelectors) => {
		return _.chain(classSelectors).map( selector => {
			return '.'+ selector;
		}).value().join(' ');
	};

	return class VisualizationPanelView extends View {
		constructor (...args) {
			super(...args);
			const options = args[0];

			if (!options.getEntries) {
				return Promise.reject('Invalid getEntries api provided');
			}
			this.getEntries = options.getEntries;
			this.selectedTab = options.selectedTab || DEFAULT_SELECTED_TAB;
			this.dashboardApi = options.dashboardApi;
			this.dndManager = this.dashboardApi.getFeature('DashboardDnd.internal');
			this.loadingIndicatorTemplate = dot.template(LoadingIndicatorTemplate || '');
			this.glassContext = options.glassContext;
			this.customVisAction = new CustomVisAction({
				dashboardApi: this.dashboardApi,
				refreshCustomVis: this._refreshCustomVisDefinitions.bind(this)
			});
			this._createViewStates();
		}

		init (...args) {
			this.events = {
				'clicktap .vis-panel-container .prop-item:not(.is-disabled)': '_onItemClick',
				'mousedown .vis-panel-container .prop-item:not(.is-disabled)': '_onItemMouseDownStartDrag',
				'keydown .vis-panel-container .prop-item:not(.is-disabled)': '_onItemKeyDown'
			};
			super.init(...args);
		}

		remove(...args) {
			if(this._dropZone) {
				this._dropZone.remove();
				this._dropZone = null;
			}
			this.dndManager = null;
			this.customVisAction.destroy();
			this.customVisAction = null;
			ReactDOM.unmountComponentAtNode(this.$el[0]);
			super.remove(...args);
		}

		render() {
			this._showLoadingIndicator();

			return this.getEntries().then(data => {
				this.items = data.items;
				this.options = data.options;
				return this._separateVisBaseOnFunctionality(data.items);
			}).then(() => {

				this.viewState.handlers = {
					componentDidMountCallback: this._componentDidMountCallback.bind(this),
					componentWillUnmountCallback: this._componentWillUnmountCallback.bind(this),
					refreshCustomVisDefinitions: this._refreshCustomVisDefinitions.bind(this),
					isSchematicType: this.isSchematicType.bind(this),
					isSDKActive: this._isSDKActive.bind(this),
					isSchematicTypePreview: this.isSchematicTypePreview.bind(this)
				};

				ReactDOM.render(
					<VisualizationTabs
						tabs={this.viewState}
						dashboardApi={this.dashboardApi}
						selectedTab={this.selectedTab}
						customVisAction={this.customVisAction}
					/>,
					this.$el[0]
				);
			}).finally(() => {
				this._hideLoadingIndicator();
				this._dropZone = this.dndManager.addDropTarget(this.$el[0], { accepts: this._accepts.bind(this) });
			});
		}

		_createViewStates () {
			this.viewState = {
				system: {
					items: []
				},
				custom: {
					preview: {
						previewItems: []
					},
					visuals: {
						items: []
					}
				}
			};
		}

		_showLoadingIndicator () {
			this.$el.append(this.loadingIndicatorTemplate({}));
			this.loadingIndicatorContainer = this.$el.find('.loadingIndicatorContainer');
			this.loadingIndicator = this.loadingIndicatorContainer.find('.loading_indicator');
			ReactDOM.render(
				<ProgressIndicator/>,
				this.loadingIndicator.get(0)
			);
		}

		_hideLoadingIndicator () {
			if (this.loadingIndicatorContainer) {
				this.loadingIndicatorContainer.remove();
				this.loadingIndicatorContainer = null;
				ReactDOM.unmountComponentAtNode(this.loadingIndicator.get(0));
			}
		}

		_accepts () {
			return true;
		}

		_componentDidMountCallback (containerSelector, visItemSelector, $node) {
			if ($node instanceof $) {
				$node.hammer();
				$node.on('hold', this._onItemHoldAndStartDrag.bind(this));
			} else {
				const selector = _geVisItemSelector([containerSelector, visItemSelector]);
				const $items = this.$el.find(selector);
				_.each($items, item => {
					const $item = $(item);
					$item.hammer();
					$item.on('hold', this._onItemHoldAndStartDrag.bind(this));
				});
			}
		}

		_componentWillUnmountCallback (containerSelector, visItemSelector, $node) {
			if ($node instanceof $) {
				$node.off('hold', this._onItemHoldAndStartDrag.bind(this));
			} else {
				const selector = _geVisItemSelector([containerSelector, visItemSelector]);
				const $items = this.$el.find(selector);
				_.each($items, item => {
					const $item = $(item);
					$item.off('hold', this._onItemHoldAndStartDrag.bind(this));
				});
			}
		}

		_refreshCustomVisDefinitions (freshAll = false) {
			if (!this.getEntries) {
				return Promise.reject('Invalid getEntries api provided');
			}

			const visDefinitionAPI = this.dashboardApi.getFeature('VisDefinitions');
			if (!this._refreshPromise) {
				this._refreshPromise = visDefinitionAPI.refresh()
					.then(this.getEntries.bind(this))
					.then((data = {}) => {
						const items = data.items || [];
						this.items = items;
						return this._separateVisBaseOnFunctionality(items);
					}).then(() => {
						this._refreshPromise = null;
						return freshAll ? this.viewState : this.viewState.custom.visuals.items;
					}).catch((err) => {
						this._refreshPromise = null;
						throw err;
					});
			}
			return this._refreshPromise;
		}

		_isSDKActive() {
			if (!this._sdkPromise ) {
				this._sdkPromise = VidaSdkUtil.isSDKActive(this.dashboardApi)
					.then((sdkIsActive) => {
						if ( sdkIsActive ) {
							if (this._refreshPromise ) {
								// prevent multiple refreshes from happening at the same time.
								return this._refreshPromise.then(this._refreshCustomVisDefinitions.bind(this, true));
							} else {
								return this._refreshCustomVisDefinitions(true);
							}
						} else {
							throw new Error('SDK is not active');
						}
					}).then(() => {
						this._sdkPromise = null;
						return true;
					}).catch(() => {
						this._sdkPromise = null;
						return false;
					});
			}
			return this._sdkPromise;
		}

		_separateVisBaseOnFunctionality (items = []) {
			const customPreviewVis = [];
			const customExtVis = [];
			const systemVis = this.viewState.system.items = [];
			const schematicBundles = [];

			let visPreview;
			let svgPreview;

			const CustomVisual_32 = this.dashboardApi.getFeature('Icons').getIcon('customVisual');

			items.forEach(item => {
				const bundle = VIPRConfig.getConfig(item.name);
				const isCustomVis = bundle && bundle.isCustomVis;
				const isSchematic = bundle && bundle.isSchematic;
				if (item.name === SKD_VIS_PREVIEW) {
					visPreview = item;
				} else if (item.name === CUS_VIS_SCHEMATIC_PREVIEW) {
					svgPreview = item;
				} else if (isCustomVis) {
					if (!item.icon) {
						//Default to generic icon
						item.icon = CustomVisual_32.id;
					}
					item.isCustomVis = isCustomVis;
					customExtVis.push(item);
				} else if (isSchematic) {
					item.isSchematic = isSchematic;
					schematicBundles.push({
						item: item,
						bundle: bundle
					});
				} else {
					systemVis.push(item);
				}
			});

			// Add visualizationPreview first
			if (visPreview) {
				customPreviewVis.push(visPreview);
			} else {
				//add a dummy visPreview just in case the user has develop permission but SDK is not running
				customPreviewVis.push({
					name: 'visualizationPreview',
					label: StringResources.get('customVisTestVisualization')
				});
			}

			//Add SVG preview second
			if (svgPreview) {
				customPreviewVis.push(svgPreview);
			}

			return this._getSchematicSVGContext(schematicBundles)
				.then(() => {
					this.viewState.custom.preview.previewItems = customPreviewVis;
					const schematicItems = schematicBundles.map(bundle => {
						return bundle.item;
					});
					this.viewState.custom.visuals.items = _.sortBy([...customExtVis, ...schematicItems], (item) => {
						return item.label;
					});
				});
		}

		_getSchematicSVGContext (schematicBundles = []) {
			const promises = [];
			schematicBundles.forEach(schematic => {
				const payload = {
					method: 'get',
					type: 'meta',
					library: schematic.bundle.id
				};
				promises.push(schematic.bundle.schematicVis.getSchematicContent(payload, this.dashboardApi));
			});
			return Promise.all(promises)
				.then((responses = []) => {
					responses.forEach((response, idx) => {
						const bundle = schematicBundles[idx];
						const metadata = JSON.parse(response) || {};
						const content = metadata.content || [];
						bundle.item.numSVGs = content.length;
						if (bundle.bundle.icon) {
							bundle.item.placeholderIcon = bundle.bundle.placeholderIcon + SchematicVis.cleanseFilename(bundle.bundle.icon);
						} else {
							const SchematicVisual_32 = this.dashboardApi.getFeature('Icons').getIcon('schematicVisual');
							bundle.item.icon = SchematicVisual_32.id;
						}
					});
				});
		}

		_getEntry (event) {
			const $node = $(event.target);
			let $target = $node.closest('.prop-item');
			const targetName = $target.data('name');
			const targetItem = this.items.find(item => {
				return item.name === targetName;
			});

			//The content contains the svg that only gets use for Drag and Drop
			return targetItem.content;
		}

		_onItemKeyDown (event) {
			if (event.keyCode === KeyCodes.ENTER || event.keyCode === KeyCodes.SPACE) {
				this._onItemClick(event);
			}
		}

		_onItemClick (event) {
			const currentTarget = event.currentTarget;
			if ($.data(currentTarget, '_clickEventReceived') !== true) {
				// Guard against adding two widgets at once during a double click.
				$.data(currentTarget, '_clickEventReceived', true);
				setTimeout(function () {
					$.data(currentTarget, '_clickEventReceived', false);
				}, 500);
				if (this.options.onItemClick) {
					var entry = this._getEntry(event);
					this._setDashboardApi(entry);
					this.options.onItemClick(entry, event);
					if (event && event.gesture) {
						event.gesture.preventDefault();
					}
				}
			}
		}

		_onStartDrag (event) {
			if (this.options.onItemStartDrag) {
				if (event.preventDefault) {
					// prevent default drag behavior
					event.preventDefault();
				}
				var entry = this._getEntry(event);
				this._setDashboardApi(entry);
				this.options.onItemStartDrag(entry, event);
			}
		}

		_setDashboardApi(widgetEntry) {
			//always fetch the DashboardAPI from the current content view
			//TODO: pass dashboardAPI from event handler inside LiveVisualizationActionHandler.js
			if(!widgetEntry.options){
				widgetEntry.options = {};
			}
			widgetEntry.options.dashboardApi = this.glassContext.appController.getCurrentContentView().getDashboardApi();
		}

		_onItemHoldAndStartDrag (event) {
			event.showAvatarImmediately = true;
			this._onStartDrag(event);
		}

		_onItemMouseDownStartDrag (event) {
			this._onStartDrag(event);
		}

		isSchematicType (item = {}) {
			return item.name === CUS_VIS_SCHEMATIC_PREVIEW || item.isSchematic;
		}

		isSchematicTypePreview (item = {}) {
			return item.name === CUS_VIS_SCHEMATIC_PREVIEW;
		}
	};
});
